/**
 * ลมยก (updrafts, docs/archive/OPEN_WORLD.md §3.3): columns of rising air at cliff feet, beside waterfalls,
 * round the stone forest's spires and over the golden summit. A glider inside one is carried up
 * toward the updraft's lift instead of sinking, easing off to a float at its top; nothing else
 * feels it. The World asks `liftAt` in its glide (world.ts, the air branch of the player's update).
 *
 * The volumes are data: a region's in `data/regions/<id>.qinggong.ts` (with one round each spire),
 * a zone's own in village-local metres (moved by the core's origin, region/core.ts). No dice, no
 * clock: the same place and height always give the same lift.
 */
import type { Updraft } from '../data/qinggong';
import { ZONES } from '../data/zones';
import { YANYU2_UPDRAFTS } from '../data/regions/yanyu2.qinggong';
import { SPIRE_UPDRAFT, YANYU_UPDRAFTS, YANYU_VILLAGE_UPDRAFTS } from '../data/regions/yanyu.qinggong';
import { floorAt, PX_METRES, type GameMap } from '../map';
import { coreOf, fromCore, regionOf } from '../region/zone';
import type { Entity } from '../world';
import type { SystemHost, WorldSystem } from './types';

export const WIND = {
  /** The metres under an updraft's top over which its lift eases off to nothing. */
  ease: 6,
  /** How quickly (per second) a glider's climb or fall turns to the updraft's. */
  grip: 3,
} as const;

/** A region's updrafts by region id (region metres), and a zone's own (village-local metres). */
const REGION_UPDRAFTS: Readonly<Record<string, readonly Updraft[]>> = { yanyu: YANYU_UPDRAFTS };
const ZONE_UPDRAFTS: Readonly<Partial<Record<string, readonly Updraft[]>>> = { yanyu: YANYU_VILLAGE_UPDRAFTS, yanyu2: YANYU2_UPDRAFTS };

/** Every updraft on a map, in the map's own metres. */
export function updraftsOf(m: GameMap): Updraft[] {
  const out: Updraft[] = [];
  const zone = ZONES[m.zone];
  // The authored core's (the village's), wherever its origin lies in this map.
  for (const u of ZONE_UPDRAFTS[m.zone] ?? []) {
    const p = fromCore(m, u.x, u.y);
    out.push({ ...u, x: p.x, y: p.y });
  }
  const id = zone?.region;
  const o = coreOf(m);
  if (id && (o.x || o.y)) {
    out.push(...(REGION_UPDRAFTS[id] ?? []));
    for (const sp of regionOf(id).sketch.spires)
      out.push({
        id: sp.id,
        x: sp.x,
        y: sp.y,
        r: sp.r + SPIRE_UPDRAFT.pad,
        top: sp.top + SPIRE_UPDRAFT.over,
        lift: SPIRE_UPDRAFT.lift,
      });
  }
  return out;
}

export class WindSystem implements WorldSystem {
  readonly name = 'wind';
  private map: GameMap | null = null;
  private list: Updraft[] = [];

  constructor(private readonly h: SystemHost) {}

  /** The updraft a point at `alt` metres above the sea is in, or null. */
  updraftAt(x: number, y: number, alt: number): Updraft | null {
    const m = this.h.world.map;
    if (this.map !== m) {
      this.map = m;
      this.list = updraftsOf(m);
    }
    for (const u of this.list) if (alt < u.top && Math.hypot(x - u.x, y - u.y) <= u.r) return u;
    return null;
  }

  /**
   * The climb (world pixels a second) a glider at `e` is drawn toward, or null outside every
   * updraft. The World turns its fall toward this at `WIND.grip` a second, without gravity.
   */
  liftAt(e: Entity): number | null {
    const alt = (floorAt(this.h.world.map, e.x, e.y) + e.z) * PX_METRES;
    const u = this.updraftAt(e.x, e.y, alt);
    if (!u) return null;
    return (u.lift * Math.min(1, (u.top - alt) / WIND.ease)) / PX_METRES;
  }
}
